import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore, useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  bubbleContainer: {
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    alignSelf: 'center',
  },
  bubbleContainerInner: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 20,
    backgroundColor: colors.solid_color_tertiary,
    marginBottom: 0,
  },
  bubble: {
    padding: 16,
    borderRadius: 12,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 64,
    minHeight: 40,
    backgroundColor: colors.background_secondary
  },

  slimContainer: {
    paddingVertical: 8,
    paddingHorizontal: 0,
    position: 'relative',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
  },
  slimContainerInner: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
  slimContent: {
    flex: 1,
  },
  slimContentWrapper: {
    flex: 1,
    flexShrink: 1,
  },
  slimAvatar: {
    width: isXS ? 20 : 30,
    height: isXS ? 20 : 30,
    borderRadius: 99,
    marginRight: isXS ? 8 : 12,
    color: colors.text_color_tertiary,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  slimContentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  slimContentUsername: {
    marginRight: 8,
  },
  
  wideContainer: {
    flexDirection: 'row',
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 0,
    alignSelf: 'center',
    width: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    maxWidth: '100%',
    position: 'relative',
  },
  wideContainerInner: {
    flex: 1,
    width: '100%',
    position: 'relative',
    height: 'auto',
  },

  messageContent: {
    marginBottom: 12,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 8,
  },
});

const MessageGhostUpsell = React.memo(() => {
  const { colors } = useTheme();
  const messageLayout = useUIStore(state => state.messageLayout);
  const { isXS } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const { openModal } = useModalStore(state => state.actions);
  const styles = useMemo(() => createStyles(colors, isXS, messageWidth), [colors, isXS, messageWidth]);

  const handleSignupClick = useCallback(() => {
    openModal('GhostSignupModal');
  }, [openModal]);

  const handleLoginClick = useCallback(() => {
    openModal('LoginModal');
  }, [openModal]);
  
  const renderContent = () => (
    <>
      <Text color="primary" size="m" style={styles.messageContent}>
        View your reply and keep the chat moving — create a free account in seconds (no credit card required).
      </Text>
      <View style={styles.buttonContainer}>
        <Button
          text="Log in"
          variant="secondary"
          size="sm"
          onPress={handleLoginClick}
        />
        <Button
          text="Sign up"
          variant="primary"
          size="sm"
          appendIcon="arrow-right"
          onPress={handleSignupClick}
        />
      </View>
    </>
  );

  if (messageLayout === MESSAGE_LAYOUT.BUBBLE) {
    return (
      <View style={styles.bubbleContainer}>
        <View style={styles.bubbleContainerInner}>
          {renderContent()}
        </View>
      </View>
    );
  }

  if (messageLayout === MESSAGE_LAYOUT.WIDE) {
    return (
      <View style={styles.wideContainer}>
        <View style={styles.wideContainerInner}>
          {renderContent()}
        </View>
      </View>
    );
  }

  // slim
  return (
    <View style={styles.slimContainer}>
      <View style={styles.slimContainerInner}>
        <View style={styles.slimAvatar}>
          <Icon name="user-circle" size={12} color={colors.text_color_tertiary} />
        </View>
        <View style={styles.slimContentWrapper}>
          <View style={styles.slimContent}>
            <View style={styles.slimContentHeader}>
              <Text color='secondary' size='n' style={styles.slimContentUsername}>
                Connect with Us
              </Text>
              <Text color='tertiary' size='xs'>
                {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </View>
            {renderContent()}
          </View>
        </View>
      </View>
    </View>
  );
});

export default MessageGhostUpsell;
